import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  Calculator,
  Clock,
  FileText,
  Plus,
  PoundSterling,
  Save,
  TrendingDown,
  TrendingUp,
  Trash2,
  Users,
} from 'lucide-react';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';

type Period = 'day' | 'week' | 'month';

interface CarerRecord {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface HourlyRate {
  caregiverId: string;
  rate: number;
}

interface TimesheetEntry {
  id: string;
  caregiverId: string;
  date: string;
  hours: number;
  scheduledHours?: number;
  realHours?: number;
  varianceMinutes?: number;
  notes: string;
  source?: 'carer-app' | 'manual';
  clientName?: string;
  status?: 'draft' | 'submitted' | 'approved' | 'paid';
  recordedBy?: string;
  recordedAt?: string;
}

interface PayrollTotal {
  caregiverId: string;
  name: string;
  email: string;
  hours: number;
  scheduledHours: number;
  realHours: number;
  varianceHours: number;
  overtimeHours: number;
  regularHours: number;
  rate: number;
  total: number;
}

interface ClientContract {
  clientName: string;
  contractName: string;
  billingRate: number;
}

interface ClientCostReport {
  clientName: string;
  contractName: string;
  hours: number;
  payrollCost: number;
  operatingCost: number;
  revenue: number;
  profit: number;
  margin: number;
}

const RATES_KEY = 'admin:carer-hourly-rates';
const TIMESHEETS_KEY = 'admin:carer-payroll-timesheets';
const STANDARD_WEEKLY_HOURS = 40;
const OVERTIME_MULTIPLIER = 1.5;
const OPERATING_COST_PER_HOUR = 3.75;
const DEFAULT_CLIENT_BILLING_RATE = 28;

const carers: CarerRecord[] = [
  { id: 'carer-adrian', name: 'Adrian Popescu', email: 'AdrianPopescu@carer.com', role: 'Senior Carer' },
  { id: 'carer-daniel', name: 'Daniel Moore', email: 'daniel.moore@fuchsiahomecare.co.uk', role: 'Care Assistant' },
  { id: 'carer-amelia', name: 'Amelia Patel', email: 'amelia.patel@fuchsiahomecare.co.uk', role: 'Care Assistant' },
  { id: 'carer-oliver', name: 'Oliver Reed', email: 'oliver.reed@fuchsiahomecare.co.uk', role: 'Care Coordinator' },
  { id: 'carer-grace', name: 'Grace Morgan', email: 'grace.morgan@fuchsiahomecare.co.uk', role: 'Care Assistant' },
];

const defaultRates: HourlyRate[] = [
  { caregiverId: 'carer-adrian', rate: 14.5 },
  { caregiverId: 'carer-daniel', rate: 15.25 },
  { caregiverId: 'carer-amelia', rate: 14.75 },
  { caregiverId: 'carer-oliver', rate: 16 },
  { caregiverId: 'carer-grace', rate: 15 },
];

const clientContracts: ClientContract[] = [
  { clientName: 'Margaret Williams', contractName: 'Great Yarmouth domiciliary care', billingRate: 29.5 },
  { clientName: 'George Taylor', contractName: 'Medication and wellbeing visits', billingRate: 28 },
  { clientName: 'Eileen Cooper', contractName: 'Personal care and meals', billingRate: 30 },
  { clientName: 'Arthur Bennett', contractName: 'Companionship and supervision', billingRate: 27.5 },
  { clientName: 'Rose Hughes', contractName: 'Evening and weekend care', billingRate: 31 },
];

const defaultTimesheets: TimesheetEntry[] = [
  { id: 'ts-001', caregiverId: 'carer-adrian', date: '2026-05-18', hours: 8, scheduledHours: 8, realHours: 8.1, varianceMinutes: 6, clientName: 'Margaret Williams', notes: 'Morning and lunch visits' },
  { id: 'ts-002', caregiverId: 'carer-adrian', date: '2026-05-19', hours: 7.5, scheduledHours: 7.5, realHours: 7.25, varianceMinutes: -15, clientName: 'George Taylor', notes: 'Personal care route' },
  { id: 'ts-003', caregiverId: 'carer-adrian', date: '2026-05-20', hours: 6, scheduledHours: 6, realHours: 6.4, varianceMinutes: 24, clientName: 'Margaret Williams', notes: 'Medication support' },
  { id: 'ts-004', caregiverId: 'carer-daniel', date: '2026-05-18', hours: 7, scheduledHours: 7, realHours: 7, varianceMinutes: 0, clientName: 'Eileen Cooper', notes: 'Companionship and meal prep' },
  { id: 'ts-005', caregiverId: 'carer-daniel', date: '2026-05-20', hours: 8.25, scheduledHours: 8, realHours: 8.25, varianceMinutes: 15, clientName: 'Arthur Bennett', notes: 'Double-up visits' },
  { id: 'ts-006', caregiverId: 'carer-amelia', date: '2026-05-17', hours: 5.5, scheduledHours: 5, realHours: 5.5, varianceMinutes: 30, clientName: 'Rose Hughes', notes: 'Weekend emergency cover' },
  { id: 'ts-007', caregiverId: 'carer-amelia', date: '2026-05-19', hours: 8, scheduledHours: 8, realHours: 8, varianceMinutes: 0, clientName: 'Eileen Cooper', notes: 'Full day care calls' },
  { id: 'ts-008', caregiverId: 'carer-oliver', date: '2026-05-16', hours: 6.75, scheduledHours: 6.5, realHours: 6.75, varianceMinutes: 15, clientName: 'Arthur Bennett', notes: 'Supervision and reviews' },
  { id: 'ts-009', caregiverId: 'carer-oliver', date: '2026-05-20', hours: 4.5, scheduledHours: 4.5, realHours: 4.2, varianceMinutes: -18, clientName: 'Margaret Williams', notes: 'Client reviews' },
  { id: 'ts-010', caregiverId: 'carer-grace', date: '2026-05-18', hours: 7.75, scheduledHours: 7.5, realHours: 7.75, varianceMinutes: 15, clientName: 'Rose Hughes', notes: 'Evening care route' },
  { id: 'ts-011', caregiverId: 'carer-grace', date: '2026-05-19', hours: 7, scheduledHours: 7, realHours: 7.1, varianceMinutes: 6, clientName: 'George Taylor', notes: 'Morning route' },
  { id: 'ts-012', caregiverId: 'carer-grace', date: '2026-04-29', hours: 8, scheduledHours: 8, realHours: 8, varianceMinutes: 0, clientName: 'Rose Hughes', notes: 'Monthly comparison sample' },
];

const currency = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

const numberFormat = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

function todayIso(): string {
  return toLocalIsoDate(new Date());
}

function toLocalIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getWeekStart(dateString: string): string {
  const date = new Date(`${dateString}T00:00:00`);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return toLocalIsoDate(date);
}

function formatDate(dateString: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${dateString}T00:00:00`));
}

function periodMatches(entryDate: string, period: Period, selectedDate: string): boolean {
  if (period === 'day') {
    return entryDate === selectedDate;
  }

  if (period === 'week') {
    return getWeekStart(entryDate) === getWeekStart(selectedDate);
  }

  return entryDate.slice(0, 7) === selectedDate.slice(0, 7);
}

function periodLabel(period: Period, selectedDate: string): string {
  if (period === 'day') {
    return formatDate(selectedDate);
  }

  if (period === 'week') {
    const weekStart = getWeekStart(selectedDate);
    const weekEnd = new Date(`${weekStart}T00:00:00`);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return `${formatDate(weekStart)} - ${formatDate(toLocalIsoDate(weekEnd))}`;
  }

  return new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${selectedDate.slice(0, 7)}-01T00:00:00`));
}

function totalHours(entries: TimesheetEntry[]): number {
  return entries.reduce((sum, entry) => sum + entry.hours, 0);
}

function totalScheduledHours(entries: TimesheetEntry[]): number {
  return entries.reduce((sum, entry) => sum + (entry.scheduledHours ?? entry.hours), 0);
}

function totalRealHours(entries: TimesheetEntry[]): number {
  return entries.reduce((sum, entry) => sum + (entry.realHours ?? entry.hours), 0);
}

function varianceHours(entry: TimesheetEntry): number {
  if (typeof entry.varianceMinutes === 'number') return entry.varianceMinutes / 60;
  return (entry.realHours ?? entry.hours) - (entry.scheduledHours ?? entry.hours);
}

function groupByWeek(entries: TimesheetEntry[]): Record<string, TimesheetEntry[]> {
  return entries.reduce<Record<string, TimesheetEntry[]>>((groups, entry) => {
    const key = getWeekStart(entry.date);
    groups[key] = [...(groups[key] ?? []), entry];
    return groups;
  }, {});
}

function calculateOvertimeHours(entries: TimesheetEntry[]): number {
  return Object.values(groupByWeek(entries)).reduce((sum, weekEntries) => {
    const weekHours = totalHours(weekEntries);
    return sum + Math.max(0, weekHours - STANDARD_WEEKLY_HOURS);
  }, 0);
}

function getClientContract(clientName?: string): ClientContract {
  return clientContracts.find((contract) => contract.clientName === clientName) ?? {
    clientName: clientName || 'Unassigned client',
    contractName: 'Unassigned / internal support',
    billingRate: DEFAULT_CLIENT_BILLING_RATE,
  };
}

function daysInMonth(dateString: string): number {
  const [year, month] = dateString.split('-').map(Number);
  return new Date(year, month, 0).getDate();
}

function monthProgress(dateString: string): number {
  const selected = new Date(`${dateString}T00:00:00`);
  const today = new Date();
  if (selected.getFullYear() === today.getFullYear() && selected.getMonth() === today.getMonth()) {
    return Math.max(1, today.getDate());
  }
  return daysInMonth(dateString);
}

export const CarerPayroll: React.FC = () => {
  const [rates, setRates] = useState<HourlyRate[]>(() => getLocalStorage(RATES_KEY, defaultRates));
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>(() =>
    getLocalStorage(TIMESHEETS_KEY, defaultTimesheets)
  );
  const [period, setPeriod] = useState<Period>('week');
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [payslipCarerId, setPayslipCarerId] = useState(carers[0]?.id ?? '');
  const [newEntry, setNewEntry] = useState({
    caregiverId: carers[0]?.id ?? '',
    date: todayIso(),
    hours: 7.5,
    notes: '',
  });

  const getRate = (caregiverId: string): number =>
    rates.find((rate) => rate.caregiverId === caregiverId)?.rate ?? 0;

  const refreshPayrollEntries = () => {
    setTimesheets(getLocalStorage(TIMESHEETS_KEY, defaultTimesheets));
  };

  useEffect(() => {
    const handleStorageUpdate = (event: StorageEvent) => {
      if (event.key === TIMESHEETS_KEY) {
        refreshPayrollEntries();
      }
    };

    window.addEventListener('storage', handleStorageUpdate);
    window.addEventListener('focus', refreshPayrollEntries);

    return () => {
      window.removeEventListener('storage', handleStorageUpdate);
      window.removeEventListener('focus', refreshPayrollEntries);
    };
  }, []);

  const periodEntries = useMemo(
    () => timesheets.filter((entry) => periodMatches(entry.date, period, selectedDate)),
    [timesheets, period, selectedDate]
  );

  const payrollTotals = useMemo<PayrollTotal[]>(() => {
    return carers.map((carer) => {
      const entries = periodEntries.filter((entry) => entry.caregiverId === carer.id);
      const hours = totalHours(entries);
      const scheduledHours = totalScheduledHours(entries);
      const realHours = totalRealHours(entries);
      const variance = entries.reduce((sum, entry) => sum + varianceHours(entry), 0);
      const overtimeHours = calculateOvertimeHours(entries);
      const regularHours = Math.max(0, hours - overtimeHours);
      const rate = getRate(carer.id);

      return {
        caregiverId: carer.id,
        name: carer.name,
        email: carer.email,
        hours,
        scheduledHours,
        realHours,
        varianceHours: variance,
        overtimeHours,
        regularHours,
        rate,
        total: regularHours * rate + overtimeHours * rate * OVERTIME_MULTIPLIER,
      };
    });
  }, [periodEntries, rates]);

  const grandTotal = payrollTotals.reduce((sum, row) => sum + row.total, 0);
  const grandHours = payrollTotals.reduce((sum, row) => sum + row.hours, 0);
  const grandScheduledHours = payrollTotals.reduce((sum, row) => sum + row.scheduledHours, 0);
  const grandRealHours = payrollTotals.reduce((sum, row) => sum + row.realHours, 0);
  const grandVarianceHours = payrollTotals.reduce((sum, row) => sum + row.varianceHours, 0);
  const grandOvertimeHours = payrollTotals.reduce((sum, row) => sum + row.overtimeHours, 0);
  const activeCarers = payrollTotals.filter((row) => row.hours > 0).length;
  const monthEntries = timesheets.filter((entry) => entry.date.slice(0, 7) === selectedDate.slice(0, 7));
  const monthPayrollCost = monthEntries.reduce((sum, entry) => sum + entry.hours * getRate(entry.caregiverId), 0);
  const payrollForecast = monthPayrollCost > 0
    ? (monthPayrollCost / monthProgress(selectedDate)) * daysInMonth(selectedDate)
    : 0;
  const rotaDifferenceCount = periodEntries.filter((entry) => Math.abs(varianceHours(entry)) >= 0.25).length;

  const chartData = payrollTotals.map((row) => ({
    name: row.name.split(' ')[0],
    Hours: Number(row.hours.toFixed(2)),
    Pay: Number(row.total.toFixed(2)),
  }));

  const clientCostReports = useMemo<ClientCostReport[]>(() => {
    const grouped = periodEntries.reduce<Record<string, TimesheetEntry[]>>((groups, entry) => {
      const clientName = entry.clientName || 'Unassigned client';
      groups[clientName] = [...(groups[clientName] ?? []), entry];
      return groups;
    }, {});

    return Object.entries(grouped)
      .map(([clientName, entries]) => {
        const contract = getClientContract(clientName);
        const hours = totalHours(entries);
        const payrollCost = entries.reduce((sum, entry) => sum + entry.hours * getRate(entry.caregiverId), 0);
        const operatingCost = hours * OPERATING_COST_PER_HOUR;
        const revenue = hours * contract.billingRate;
        const profit = revenue - payrollCost - operatingCost;
        const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

        return {
          clientName,
          contractName: contract.contractName,
          hours,
          payrollCost,
          operatingCost,
          revenue,
          profit,
          margin,
        };
      })
      .sort((a, b) => b.revenue - a.revenue);
  }, [periodEntries, rates]);

  const totalRevenue = clientCostReports.reduce((sum, row) => sum + row.revenue, 0);
  const totalOperatingCost = clientCostReports.reduce((sum, row) => sum + row.operatingCost, 0);
  const totalProfit = clientCostReports.reduce((sum, row) => sum + row.profit, 0);

  const saveRates = () => {
    setLocalStorage(RATES_KEY, rates);
    toast.success('Hourly rates saved');
  };

  const updateRate = (caregiverId: string, value: string) => {
    const rate = Number(value);
    setRates((current) =>
      current.map((item) =>
        item.caregiverId === caregiverId
          ? { ...item, rate: Number.isFinite(rate) ? Math.max(0, rate) : 0 }
          : item
      )
    );
  };

  const addTimesheetEntry = () => {
    if (!newEntry.caregiverId || !newEntry.date || newEntry.hours <= 0) {
      toast.error('Select a carer, date, and valid hours');
      return;
    }

    const nextEntries = [
      {
        id: `ts-${Date.now()}`,
        caregiverId: newEntry.caregiverId,
        date: newEntry.date,
        hours: Number(newEntry.hours),
        scheduledHours: Number(newEntry.hours),
        realHours: Number(newEntry.hours),
        varianceMinutes: 0,
        notes: newEntry.notes.trim() || 'Manual payroll entry',
        source: 'manual' as const,
        recordedAt: new Date().toISOString(),
      },
      ...timesheets,
    ];

    setTimesheets(nextEntries);
    setLocalStorage(TIMESHEETS_KEY, nextEntries);
    toast.success('Hours added to payroll database');
    setNewEntry((current) => ({ ...current, hours: 7.5, notes: '' }));
  };

  const deleteTimesheetEntry = (entryId: string) => {
    const nextEntries = timesheets.filter((entry) => entry.id !== entryId);
    setTimesheets(nextEntries);
    setLocalStorage(TIMESHEETS_KEY, nextEntries);
    toast.success('Payroll entry removed');
  };

  const generatePayslipPdf = () => {
    const carer = carers.find((item) => item.id === payslipCarerId);
    if (!carer) {
      toast.error('Select a carer first');
      return;
    }

    const entries = periodEntries
      .filter((entry) => entry.caregiverId === carer.id)
      .sort((a, b) => a.date.localeCompare(b.date));
    const rate = getRate(carer.id);
    const hours = totalHours(entries);
    const scheduledHours = totalScheduledHours(entries);
    const realHours = totalRealHours(entries);
    const variance = entries.reduce((sum, entry) => sum + varianceHours(entry), 0);
    const overtimeHours = calculateOvertimeHours(entries);
    const regularHours = Math.max(0, hours - overtimeHours);
    const total = regularHours * rate + overtimeHours * rate * OVERTIME_MULTIPLIER;
    const logoUrl = `${window.location.origin}/images/fuchsia-logo-full.png`;
    const printableWindow = window.open('', '_blank', 'width=900,height=1100');

    if (!printableWindow) {
      toast.error('Allow pop-ups to generate the PDF');
      return;
    }

    const rows =
      entries.length > 0
        ? entries
            .map(
              (entry) => `
                <tr>
                  <td>${formatDate(entry.date)}</td>
                  <td>${entry.clientName ? `${entry.clientName} - ` : ''}${entry.notes}</td>
                  <td>${numberFormat.format(entry.scheduledHours ?? entry.hours)}</td>
                  <td>${numberFormat.format(entry.realHours ?? entry.hours)}</td>
                  <td>${numberFormat.format(entry.hours)}</td>
                  <td>${currency.format(rate)}</td>
                  <td>${currency.format(entry.hours * rate)}</td>
                </tr>
              `
            )
            .join('')
        : '<tr><td colspan="7">No hours recorded for this period.</td></tr>';

    printableWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${carer.name} Payroll ${periodLabel(period, selectedDate)}</title>
          <style>
            * { box-sizing: border-box; }
            body { margin: 0; font-family: Arial, sans-serif; color: #111827; background: #f8fafc; }
            .page { width: 210mm; min-height: 297mm; margin: 0 auto; background: white; padding: 22mm; }
            .header { display: flex; align-items: center; justify-content: space-between; gap: 24px; border-bottom: 3px solid #6c3a80; padding-bottom: 18px; }
            .logo { width: 230px; max-height: 110px; object-fit: contain; }
            h1 { margin: 0; color: #5e3474; font-size: 28px; }
            h2 { margin: 24px 0 8px; font-size: 18px; color: #111827; }
            p { margin: 4px 0; line-height: 1.45; }
            .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 22px; }
            .box { border: 1px solid #d8dce4; border-radius: 8px; padding: 14px; }
            table { width: 100%; border-collapse: collapse; margin-top: 18px; }
            th, td { border: 1px solid #d8dce4; padding: 10px; text-align: left; font-size: 13px; }
            th { background: #6c3a80; color: white; }
            .total { display: flex; justify-content: flex-end; margin-top: 20px; }
            .total-box { min-width: 290px; border: 2px solid #6c3a80; border-radius: 8px; padding: 16px; }
            .total-row { display: flex; justify-content: space-between; gap: 20px; margin: 8px 0; }
            .grand { font-size: 20px; font-weight: 700; color: #5e3474; border-top: 1px solid #d8dce4; padding-top: 12px; margin-top: 12px; }
            .footer { margin-top: 36px; font-size: 12px; color: #475569; border-top: 1px solid #d8dce4; padding-top: 14px; }
            @media print {
              body { background: white; }
              .page { margin: 0; box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <main class="page">
            <section class="header">
              <div>
                <h1>Carer Payroll Statement</h1>
                <p><strong>FUCHSIA HOME CARE CATALIN</strong></p>
                <p>Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN</p>
                <p>Tel: 01493738488 | On Call: 07443145811</p>
                <p>Email: lowestoft@fuchsiahomecare.co.uk</p>
              </div>
              <img class="logo" src="${logoUrl}" alt="Fuchsia Homecare logo" />
            </section>

            <section class="meta">
              <div class="box">
                <h2>Carer</h2>
                <p><strong>${carer.name}</strong></p>
                <p>${carer.email}</p>
                <p>${carer.role}</p>
              </div>
              <div class="box">
                <h2>Pay Period</h2>
                <p><strong>${periodLabel(period, selectedDate)}</strong></p>
                <p>Period type: ${period}</p>
                <p>Hourly rate: ${currency.format(rate)}</p>
              </div>
            </section>

            <h2>Hours Worked</h2>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Notes</th>
                  <th>Scheduled</th>
                  <th>Real</th>
                  <th>Hours</th>
                  <th>Rate</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>

            <section class="total">
              <div class="total-box">
                <div class="total-row"><span>Total hours</span><strong>${numberFormat.format(hours)}</strong></div>
                <div class="total-row"><span>Scheduled hours</span><strong>${numberFormat.format(scheduledHours)}</strong></div>
                <div class="total-row"><span>Real hours</span><strong>${numberFormat.format(realHours)}</strong></div>
                <div class="total-row"><span>Rota variance</span><strong>${numberFormat.format(variance)}</strong></div>
                <div class="total-row"><span>Overtime hours</span><strong>${numberFormat.format(overtimeHours)}</strong></div>
                <div class="total-row"><span>Hourly rate</span><strong>${currency.format(rate)}</strong></div>
                <div class="total-row grand"><span>Total salary</span><span>${currency.format(total)}</span></div>
              </div>
            </section>

            <p class="footer">
              Generated from the Fuchsia Home Care administrator payroll page. Please verify final payroll figures before payment.
            </p>
          </main>
        </body>
      </html>
    `);
    printableWindow.document.close();
    printableWindow.focus();
    window.setTimeout(() => printableWindow.print(), 300);
  };

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-5">
            <img
              src="/images/fuchsia-logo-mark.png"
              alt="FUCHSIA HOME CARE CATALIN"
              className="h-20 w-20 rounded-lg object-contain"
            />
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                Administrator Payroll
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Carer Payroll Calculator</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Set hourly rates, automatically import worked hours from the carer portal, calculate salary by day, week or month, and generate a PDF payroll statement.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={saveRates}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#5e3474]"
          >
            <Save className="h-4 w-4" />
            Save Rates
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Selected Period</p>
            <Calculator className="h-5 w-5 text-[#6c3a80]" />
          </div>
          <p className="mt-3 text-2xl font-bold text-slate-950">{periodLabel(period, selectedDate)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Total Hours</p>
            <Clock className="h-5 w-5 text-[#6c3a80]" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{numberFormat.format(grandHours)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Active Carers</p>
            <Users className="h-5 w-5 text-[#6c3a80]" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{activeCarers}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Salary Total</p>
            <PoundSterling className="h-5 w-5 text-[#6c3a80]" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{currency.format(grandTotal)}</p>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Rota vs Real</p>
            <AlertTriangle className={`h-5 w-5 ${Math.abs(grandVarianceHours) >= 1 ? 'text-amber-600' : 'text-emerald-600'}`} />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{numberFormat.format(grandVarianceHours)}h</p>
          <p className="mt-1 text-xs text-slate-500">
            Scheduled {numberFormat.format(grandScheduledHours)}h / real {numberFormat.format(grandRealHours)}h. {rotaDifferenceCount} variance alert{rotaDifferenceCount === 1 ? '' : 's'}.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Overtime Detected</p>
            <Clock className={`h-5 w-5 ${grandOvertimeHours > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{numberFormat.format(grandOvertimeHours)}h</p>
          <p className="mt-1 text-xs text-slate-500">Threshold: {STANDARD_WEEKLY_HOURS}h/week, paid at {OVERTIME_MULTIPLIER}x in projections.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Monthly Payroll Forecast</p>
            <TrendingUp className="h-5 w-5 text-[#6c3a80]" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{currency.format(payrollForecast)}</p>
          <p className="mt-1 text-xs text-slate-500">Estimated from saved payroll hours in {periodLabel('month', selectedDate)}.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Profit / Loss</p>
            {totalProfit >= 0 ? <TrendingUp className="h-5 w-5 text-emerald-600" /> : <TrendingDown className="h-5 w-5 text-red-600" />}
          </div>
          <p className={`mt-3 text-3xl font-bold ${totalProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            {currency.format(totalProfit)}
          </p>
          <p className="mt-1 text-xs text-slate-500">Revenue {currency.format(totalRevenue)} minus payroll and operating costs.</p>
        </div>
      </section>

      <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-sm leading-6 text-emerald-900">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="font-bold">Automatic carer app sync</p>
            <p className="mt-1">
              When a carer checks out of a visit or submits the weekly timesheet, those hours are added automatically here for salary calculation.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              refreshPayrollEntries();
              toast.success('Payroll entries refreshed from carer app');
            }}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-emerald-300 bg-white px-4 py-2 font-semibold text-emerald-800 transition hover:bg-emerald-100"
          >
            <Clock className="h-4 w-4" />
            Refresh Carer Hours
          </button>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-950">Hourly Rates</h2>
              <p className="mt-1 text-sm text-slate-600">Administrator can set how much each carer earns per hour.</p>
            </div>
            <button
              type="button"
              onClick={saveRates}
              className="inline-flex items-center gap-2 rounded-md border border-[#6c3a80]/30 px-3 py-2 text-sm font-semibold text-[#5e3474] transition hover:bg-[#6c3a80]/5"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="py-3 pr-4">Carer</th>
                  <th className="py-3 pr-4">Email</th>
                  <th className="py-3">Hourly Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {carers.map((carer) => (
                  <tr key={carer.id}>
                    <td className="py-3 pr-4 font-semibold text-slate-950">{carer.name}</td>
                    <td className="py-3 pr-4 text-slate-600">{carer.email}</td>
                    <td className="py-3">
                      <div className="flex w-36 items-center rounded-md border border-slate-300 bg-white px-2">
                        <span className="text-slate-500">£</span>
                        <input
                          type="number"
                          min="0"
                          step="0.25"
                          value={getRate(carer.id)}
                          onChange={(event) => updateRate(carer.id, event.target.value)}
                          className="w-full border-0 bg-transparent px-2 py-2 text-sm font-semibold text-slate-950 outline-none"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-950">Add Worked Hours</h2>
          <p className="mt-1 text-sm text-slate-600">Manual entries are still available for corrections; carer app hours are imported automatically.</p>

          <div className="mt-5 grid gap-3 md:grid-cols-2">
            <label className="text-sm font-medium text-slate-700">
              Carer
              <select
                value={newEntry.caregiverId}
                onChange={(event) => setNewEntry((current) => ({ ...current, caregiverId: event.target.value }))}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950"
              >
                {carers.map((carer) => (
                  <option key={carer.id} value={carer.id}>
                    {carer.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium text-slate-700">
              Date
              <input
                type="date"
                value={newEntry.date}
                onChange={(event) => setNewEntry((current) => ({ ...current, date: event.target.value }))}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-950"
              />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Hours Worked
              <input
                type="number"
                min="0"
                step="0.25"
                value={newEntry.hours}
                onChange={(event) =>
                  setNewEntry((current) => ({ ...current, hours: Number(event.target.value) }))
                }
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-950"
              />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Notes
              <input
                type="text"
                value={newEntry.notes}
                onChange={(event) => setNewEntry((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Visits, route, supervision..."
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-950"
              />
            </label>
          </div>

          <button
            type="button"
            onClick={addTimesheetEntry}
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#5e3474]"
          >
            <Plus className="h-4 w-4" />
            Add Hours
          </button>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-950">Salary Calculation</h2>
            <p className="mt-1 text-sm text-slate-600">
              Salary is calculated automatically from verified check-in/check-out payroll hours. Overtime is highlighted before payroll is approved.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="inline-flex rounded-md border border-slate-300 bg-white p-1">
              {(['day', 'week', 'month'] as Period[]).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setPeriod(item)}
                  className={`rounded px-3 py-1.5 text-sm font-semibold capitalize transition ${
                    period === item ? 'bg-[#6c3a80] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-950"
            />
          </div>
        </div>

        <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_0.85fr]">
          <div className="h-80 rounded-lg border border-slate-200 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="Hours" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="Pay" fill="#6c3a80" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Carer</th>
                  <th className="px-4 py-3">Scheduled</th>
                  <th className="px-4 py-3">Real</th>
                  <th className="px-4 py-3">Hours</th>
                  <th className="px-4 py-3">Variance</th>
                  <th className="px-4 py-3">Overtime</th>
                  <th className="px-4 py-3">Rate</th>
                  <th className="px-4 py-3">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {payrollTotals.map((row) => (
                  <tr key={row.caregiverId}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-950">{row.name}</p>
                      <p className="text-xs text-slate-500">{row.email}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{numberFormat.format(row.scheduledHours)}</td>
                    <td className="px-4 py-3 text-slate-700">{numberFormat.format(row.realHours)}</td>
                    <td className="px-4 py-3 text-slate-700">{numberFormat.format(row.hours)}</td>
                    <td className={`px-4 py-3 font-semibold ${Math.abs(row.varianceHours) >= 0.25 ? 'text-amber-700' : 'text-slate-700'}`}>
                      {numberFormat.format(row.varianceHours)}
                    </td>
                    <td className={`px-4 py-3 font-semibold ${row.overtimeHours > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                      {numberFormat.format(row.overtimeHours)}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{currency.format(row.rate)}</td>
                    <td className="px-4 py-3 font-bold text-[#5e3474]">{currency.format(row.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-950">Cost Per Client / Contract</h2>
              <p className="mt-1 text-sm text-slate-600">
                Revenue is estimated from client contract rate. Profit/loss subtracts payroll and operating cost per care hour.
              </p>
            </div>
            <div className="rounded-md bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
              Operating cost: {currency.format(OPERATING_COST_PER_HOUR)}/h
            </div>
          </div>

          <div className="mt-5 overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Client / Contract</th>
                  <th className="px-4 py-3">Hours</th>
                  <th className="px-4 py-3">Revenue</th>
                  <th className="px-4 py-3">Payroll</th>
                  <th className="px-4 py-3">Operating</th>
                  <th className="px-4 py-3">Profit / Loss</th>
                  <th className="px-4 py-3">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {clientCostReports.length > 0 ? clientCostReports.map((row) => (
                  <tr key={row.clientName}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-950">{row.clientName}</p>
                      <p className="text-xs text-slate-500">{row.contractName}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{numberFormat.format(row.hours)}</td>
                    <td className="px-4 py-3 text-slate-700">{currency.format(row.revenue)}</td>
                    <td className="px-4 py-3 text-slate-700">{currency.format(row.payrollCost)}</td>
                    <td className="px-4 py-3 text-slate-700">{currency.format(row.operatingCost)}</td>
                    <td className={`px-4 py-3 font-bold ${row.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {currency.format(row.profit)}
                    </td>
                    <td className={`px-4 py-3 font-semibold ${row.margin >= 15 ? 'text-emerald-700' : row.margin >= 0 ? 'text-amber-700' : 'text-red-700'}`}>
                      {numberFormat.format(row.margin)}%
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No client cost data for the selected period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-950">Cost Control Alerts</h2>
          <div className="mt-5 space-y-3">
            {[
              {
                title: 'Rota difference',
                text: `${rotaDifferenceCount} visit record${rotaDifferenceCount === 1 ? '' : 's'} differ by at least 15 minutes from the rota.`,
                active: rotaDifferenceCount > 0,
              },
              {
                title: 'Overtime risk',
                text: grandOvertimeHours > 0
                  ? `${numberFormat.format(grandOvertimeHours)} overtime hour${grandOvertimeHours === 1 ? '' : 's'} detected in the selected period.`
                  : 'No overtime detected for the selected period.',
                active: grandOvertimeHours > 0,
              },
              {
                title: 'Monthly forecast',
                text: `Estimated payroll before month end is ${currency.format(payrollForecast)} based on current saved hours.`,
                active: payrollForecast > monthPayrollCost,
              },
              {
                title: 'Profit/loss review',
                text: totalProfit >= 0
                  ? `Selected period is profitable by ${currency.format(totalProfit)}.`
                  : `Selected period is losing ${currency.format(Math.abs(totalProfit))}; review contract rate, rota and overtime.`,
                active: totalProfit < 0,
              },
            ].map((alert) => (
              <div
                key={alert.title}
                className={`rounded-lg border p-4 ${
                  alert.active ? 'border-amber-200 bg-amber-50 text-amber-950' : 'border-emerald-200 bg-emerald-50 text-emerald-950'
                }`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`mt-0.5 h-5 w-5 ${alert.active ? 'text-amber-700' : 'text-emerald-700'}`} />
                  <div>
                    <p className="font-semibold">{alert.title}</p>
                    <p className="mt-1 text-sm leading-6">{alert.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.85fr_1.15fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-950">Create PDF Payroll Statement</h2>
          <p className="mt-1 text-sm text-slate-600">
            Select a carer and generate a print-ready PDF for the current selected period.
          </p>

          <label className="mt-5 block text-sm font-medium text-slate-700">
            Carer
            <select
              value={payslipCarerId}
              onChange={(event) => setPayslipCarerId(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950"
            >
              {carers.map((carer) => (
                <option key={carer.id} value={carer.id}>
                  {carer.name}
                </option>
              ))}
            </select>
          </label>

          <div className="mt-4 rounded-md bg-[#6c3a80]/10 p-4 text-sm leading-6 text-[#4b255f]">
            PDF period: <strong>{periodLabel(period, selectedDate)}</strong>. The PDF includes company logo, worked hours, hourly rate, and total salary.
          </div>

          <button
            type="button"
            onClick={generatePayslipPdf}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#5e3474]"
          >
            <FileText className="h-4 w-4" />
            Generate PDF
          </button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-950">Payroll Database Entries</h2>
          <p className="mt-1 text-sm text-slate-600">Latest saved worked-hours records used by the payroll calculator.</p>
          <div className="mt-5 max-h-96 overflow-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Carer</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Scheduled</th>
                  <th className="px-4 py-3">Real</th>
                  <th className="px-4 py-3">Hours</th>
                  <th className="px-4 py-3">Variance</th>
                  <th className="px-4 py-3">Notes</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {timesheets.map((entry) => {
                  const carer = carers.find((item) => item.id === entry.caregiverId);
                  return (
                    <tr key={entry.id}>
                      <td className="px-4 py-3 text-slate-700">{formatDate(entry.date)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-950">{carer?.name ?? 'Unknown carer'}</td>
                      <td className="px-4 py-3 text-slate-700">{entry.clientName ?? 'Unassigned'}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                          entry.source === 'carer-app'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          {entry.source === 'carer-app' ? 'Carer app' : 'Manual admin'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{numberFormat.format(entry.scheduledHours ?? entry.hours)}</td>
                      <td className="px-4 py-3 text-slate-700">{numberFormat.format(entry.realHours ?? entry.hours)}</td>
                      <td className="px-4 py-3 text-slate-700">{numberFormat.format(entry.hours)}</td>
                      <td className={`px-4 py-3 font-semibold ${Math.abs(varianceHours(entry)) >= 0.25 ? 'text-amber-700' : 'text-slate-700'}`}>
                        {numberFormat.format(varianceHours(entry))}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {entry.clientName ? `${entry.clientName} - ` : ''}{entry.notes}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => deleteTimesheetEntry(entry.id)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-red-700 transition hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};
